/* ---------------------------------
-----  dimension variables --------
---------------------------------- */
/* ------  standard elements padding & margin  ------- */
/* spacing */
/**
**  Official Font sizes
------------------------------- */
/**
** Line-heights
------------------------------- */
/*
  Mixin to progressively-enhance :focus-visible, keeping :focus where not supported
  Add an arbitrary list of selectors into the arguments, and the styling in the block.
  I you pass no selectors to the mixin the parent ("ampersand") operator is used instead.

  There are two mixins:
  - focus-visible: a generic focus-visible, giving you "slots" to decide what to render for :focus, :focus reset, and :focus-visible
  - focus-visible-outline: a focus-visible, which only resets the outline. If you don't need more custom resets, use this one!

  Use focus-visible:
  The key thing here is checking which slot is being rendered.
  For example, you can reset box-shadow here, or anything else.

  @include focus-visible using ($slot) {
      @if $slot == focus {
          outline: 2px solid transparent;
          box-shadow: 0 0 0 4px blue;
      }
      @if $slot == focusReset {
          box-shadow: none;
      }
      @if $slot == focusVisible {
          box-shadow: 0 0 0 4px blue;
      }
  }

  Use focus-visible-outline:
  @include focus-visible-outline("button", "a[href]") {
      outline: 2px solid blue;
  }

  Resulting CSS (compressed):

  button:focus,
  a[href]:focus {
      outline: 2px solid blue;
  }

  button:focus:not(:focus-visible),
  a[href]:focus:not(:focus-visible) {
      outline: none;
  }
  button:focus-visible,
  a[href]:focus-visible {
      outline: 2px solid blue;
  }
*/
/**
@prop --ux-input-height: the height of the field itself (without labels)
@prop --ux-input-font-size: font-size of the input
@prop --ux-input-font-weight: font-weight of the input
@prop --ux-input-line-height: line-height of the input
@prop --ux-input-padding-horizontal: horizontal inner padding
@prop --ux-input-padding-vertical: vertical inner padding
@prop --ux-input-padding: combined vertical and horizontal padding
@prop --ux-input-spacing: spacing between field elements
@prop --ux-input-outline-width: outline thickness used when the field has focus
@prop --ux-input-border-width: border thickness of the input
@prop --ux-input-border-color: border-color of the input
@prop --ux-input-error-color: text color of the input in error state
@prop --ux-input-error-border-color: border-color of the input in error state
@prop --ux-input-placeholder-color: color of the placeholder text
@prop --ux-input-placeholder-font-weight: font-weight of the placeholder text
@prop --ux-input-focus-border-color: border-color of the input when focussed
@prop --ux-input-focus-text-color: text color of the input when focussed
@prop --ux-input-hover-bg: background-color of the input when hovered
@prop --ux-input-hover-border-color: border-color of the input when hovered
@prop --ux-input-hover-text-color: text color of the input when hovered
@prop --ux-input-active-bg: background-color of the input when active
@prop --ux-input-active-border-color: border-color of the input when active
@prop --ux-input-active-text-color: text color of the input when active
@prop --ux-input-disabled-bg: background-color when disabled
@prop --ux-input-disabled-border-color: border-color when disabled
@prop --ux-input-disabled-text-color: text color when disabled
@prop --ux-input-disabled-opacity: opacity of the input when disabled
@prop --ux-input-hint-margin: outer margin of the hint block below the input
@prop --ux-input-hint-color: color of the hint text
@prop --ux-input-hint-font-size: font-size of the hint text
@prop --ux-input-hint-font-weight: font-weight of the hint text
@prop --ux-input-hint-line-height: line-height of the hint text
@prop --ux-input-icon-size: size of the icon displayed inside the field
 */
.ux-input-field {
  box-sizing: border-box;
  display: block;
  margin: 0;
  max-width: 100%;
  position: relative;
}
.ux-input-field--disabled {
  pointer-events: none;
}
.ux-input-field--hidden {
  display: none;
}
.ux-input-field__field {
  align-items: stretch;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}
.ux-input-field__controls {
  background: var(--ux-input-background-color);
  border: var(--ux-input-border-width) solid var(--ux-input-border-color);
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  flex-wrap: nowrap;
  height: var(--ux-input-height);
  position: relative;
}
.ux-input-field__controls:hover {
  background: var(--ux-input-hover-bg);
  border-color: var(--ux-input-hover-border-color);
  color: var(--ux-input-hover-text-color);
}
.ux-input-field__controls:active {
  background: var(--ux-input-active-bg);
  border-color: var(--ux-input-active-border-color);
  color: var(--ux-input-active-text-color);
}
.ux-input-field--error .ux-input-field__controls {
  border-color: var(--ux-input-error-border-color);
}
.ux-input-field--error:hover .ux-input-field__controls {
  border-color: var(--ux-input-error-hover-border-color);
}
.ux-input-field--focus .ux-input-field__controls {
  color: var(--ux-input-focus-text-color);
  outline: var(--ux-input-outline-width) solid var(--ux-input-focus-border-color);
  outline-offset: var(--ux-input-outline-offset);
}
.ux-input-field--disabled .ux-input-field__controls {
  border-color: var(--ux-input-disabled-border-color);
  opacity: var(--ux-input-disabled-opacity);
}
.ux-input-field__input.ux-input {
  background-color: transparent;
  border: 0 none transparent;
  color: inherit;
  display: inline-block;
  flex: 1 1 auto;
  font-family: var(--ux-font);
  font-weight: var(--ux-input-font-weight);
  height: 100%;
  line-height: var(--ux-input-line-height);
  padding: var(--ux-input-padding);
}
.ux-input-field__input.ux-input::placeholder {
  color: var(--ux-input-placeholder-color);
  font-weight: var(--ux-input-placeholder-font-weight);
}
.ux-input-field--clearable .ux-input-field__input.ux-input {
  padding-right: 0;
}
.ux-input-field--numeric .ux-input-field__input.ux-input {
  text-align: right;
}
.ux-input-field--clearable .ux-input-field__input.ux-input, .ux-input-field--numeric .ux-input-field__input.ux-input, .ux-input-field--unit .ux-input-field__input.ux-input, .ux-input-field--icon .ux-input-field__input.ux-input {
  padding-right: var(--ux-input-spacing);
}
.ux-input-field--disabled .ux-input-field__input.ux-input {
  background: var(--ux-input-disabled-bg);
  border-color: var(--ux-input-disabled-border-color);
  color: var(--ux-input-disabled-text-color);
}
.ux-input-field__icon {
  align-items: center;
  display: flex;
  justify-content: center;
  padding-right: var(--ux-input-padding-horizontal);
}
.ux-input-field__icon ux-icon {
  font-size: var(--ux-input-icon-size);
  height: var(--ux-input-icon-size);
}
.ux-input-field__hint {
  display: block;
  font-size: var(--ux-input-hint-font-size);
  font-weight: var(--ux-input-hint-font-weight);
  line-height: var(--ux-input-hint-line-height);
  margin: var(--ux-input-hint-margin);
  padding: var(--ux-input-hint-padding);
}
.ux-input-field__clear {
  align-items: center;
  background: none;
  border: 0;
  color: var(--ux-input-clear-color);
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  margin: 0;
  outline: 0;
  padding: 0 1em 0 0.5em;
}
.ux-input-field__clear svg {
  fill: currentColor;
  pointer-events: auto;
}
.ux-input-field__unit {
  align-items: center;
  box-sizing: border-box;
  color: var(--ux-input-unit-color, var(--ux-input-color));
  display: flex;
  flex: 0 0 auto;
  font-size: var(--ux-input-unit-font-size, var(--ux-input-font-size));
  font-weight: var(--ux-input-unit-font-weight, var(--ux-input-font-weight));
  height: 100%;
  justify-content: center;
  line-height: var(--ux-input-unit-line-height, inherit);
  padding: 0 var(--ux-input-padding-horizontal) 0 var(--ux-input-spacing);
  position: relative;
  white-space: nowrap;
}
.ux-input-field__unit::before {
  background: var(--ux-input-border-color);
  bottom: var(--ux-input-padding-vertical);
  content: "";
  left: 0;
  position: absolute;
  top: var(--ux-input-padding-vertical);
  width: 1px;
}
.ux-input-field__buttons {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
}